import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import type { OfferPricingData } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useMaldivesOffersPricing'
import useCharterBundlePricing from 'hooks/CharterFlights/useCharterBundlePricing'
import type { CharterBundlePricingInput } from 'hooks/CharterFlights/useCharterBundlePricing'

interface Props {
  offer: App.Offer | App.OfferSummary
  pricing: OfferPricingData
  isPricePerPerson?: boolean
  discountPercentage?: number
  nights?: number
}

function mapSearchPricingToInput(pricing: OfferPricingData, discountPercentage?: number): CharterBundlePricingInput {
  const value = pricing.hotelValue && pricing.flightTotalPrice
    ? pricing.hotelValue + pricing.flightTotalPrice
    : undefined
  return {
    price: pricing.bundleTotalPrice,
    memberPrice: pricing.bundleMemberTotalPrice,
    value,
    discountPercent: discountPercentage ?? pricing.discountPercent,
    totalGuests: pricing.totalGuests,
  }
}

function CharterBundlePricing({ offer, pricing, isPricePerPerson, discountPercentage, nights }: Props) {
  const intl = useIntl()
  const input = useMemo(
    () => mapSearchPricingToInput(pricing, discountPercentage),
    [pricing, discountPercentage],
  )
  const {
    displayPrice,
    memberDisplayPrice,
    valuePrice,
    discountPercentage: resolvedDiscountPercentage,
    guestCount,
    showMemberPricing,
  } = useCharterBundlePricing(input, isPricePerPerson)

  const priceElement = useMemo(() => {
    if (showMemberPricing) {
      return (
        <LuxPlusPriceStack
          price={displayPrice ?? 0}
          memberPrice={memberDisplayPrice}
          saleUnit={isPricePerPerson ? 'person' : 'total'}
          size="L"
        />
      )
    }
    return (
      <PriceRowPrice
        size="L"
        price={displayPrice ?? 0}
        saleUnit={isPricePerPerson ? 'person' : undefined}
      />
    )
  }, [showMemberPricing, displayPrice, memberDisplayPrice, isPricePerPerson])

  const roomCount = pricing.roomBreakdown.length || 1
  const roomCaption = isPricePerPerson
    ? intl.formatMessage(
        {
          id: 'Uv5Ws/kAoN',
          defaultMessage: '{guestCount, plural, one {# person} other {# people}}',
          description: 'Guest count caption for charter bundle pricing per person',
        },
        { guestCount },
      )
    : intl.formatMessage(
        {
          id: '94mAZcswvt',
          defaultMessage: '{roomCount, plural, one {# room} other {# rooms}}',
          description: 'Room count caption for charter bundle pricing total',
        },
        { roomCount },
      )

  const resolvedNights = nights ?? pricing.allocatedPackages[0]?.package?.duration

  // TODO: i18n — complex pricing caption with trailing ' from' and ' & flights' fragments
  const nightsAndFlightsFromLabel = resolvedNights
    ? intl.formatMessage(
        {
          id: 'FyZfb0XtR5',
          defaultMessage: '{resolvedNights, plural, one {# night} other {# nights}} & flights from',
          description: 'Charter bundle price caption showing nights and flights',
        },
        { resolvedNights },
      )
    : undefined
  const fromLabel = intl.formatMessage({
    id: 'JSMgIzowLx',
    defaultMessage: 'From',
    description: 'Charter bundle price caption prefix for per-person pricing',
  })
  const totalFromLabel = intl.formatMessage({
    id: '+J1OGWHAvD',
    defaultMessage: 'Total from',
    description: 'Charter bundle price caption prefix for total pricing',
  })
  const priceCaption = nightsAndFlightsFromLabel ?? (isPricePerPerson ? fromLabel : totalFromLabel)

  return (
    <Group direction="vertical" gap={4} fullWidth>
      <PriceRowPriceCaption>
        {priceCaption}
      </PriceRowPriceCaption>
      {priceElement}
      {valuePrice && resolvedDiscountPercentage && resolvedDiscountPercentage > 0 && (
        <PriceRowValueDiscountWithCaption
          size="M"
          originalValue={valuePrice}
          discountPercentage={resolvedDiscountPercentage}
        />
      )}
      <PriceRowCaption>{roomCaption}</PriceRowCaption>
      <PriceRowTaxesAndFees />
      <PriceRowAgentHubCommission
        productType={offer.productType}
        offerId={offer.id}
      />
    </Group>
  )
}

export default CharterBundlePricing
